import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import {
  RemoteDiagnosticsControlUnit,
  RemoteDiagnosticsStore,
  RemoteDiagnosticsVddListParameter,
} from '@store';
import { Subject, timer } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { isEqual } from 'lodash-es';
import { TranslateService } from '@ngx-translate/core';
import { GTMService } from '@services';
import { ActivatedRoute } from '@angular/router';
import { RemoteDiagnosticsUiService } from '../remote-diagnostics-ui.service';
import { RemoteDiagnosticsMappers } from '../../../../../@store/remote-diagnostics/remote-diagnostics.mappers';
import { VDDParameterViewValue } from '../diagnostics-constants';
import { DiagnosticsHelpers } from '../diagnostics-helpers';
import { Option } from '../../../../../../projects/ui/components/dropdown-autocomplete/dropdown-autocomplete.component';

@Component({
  selector: 'app-diagnostics-vdd-selection-modal',
  templateUrl: './diagnostics-vdd-selection-modal.component.html',
  styleUrls: ['./diagnostics-vdd-selection-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiagnosticsVddSelectionModalComponent implements OnInit, OnDestroy {
  gatewaySerial: string;
  currentControlUnit: RemoteDiagnosticsControlUnit;
  availableParametersExceptSelected: VDDParameterViewValue[] = [];
  selectedVdds: RemoteDiagnosticsVddListParameter[] = [];
  loading: boolean;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  constructor(
    protected dialogRef: MatDialogRef<DiagnosticsVddSelectionModalComponent>,
    protected remoteDiagnosticsStore: RemoteDiagnosticsStore,
    protected remoteDiagnosticsUiService: RemoteDiagnosticsUiService,
    protected remoteDiagnosticsMappers: RemoteDiagnosticsMappers,
    private activatedRoute: ActivatedRoute,
    private gtmService: GTMService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.gatewaySerial = this.activatedRoute.snapshot.queryParams['gatewaySerial'];

    this.remoteDiagnosticsStore
      .getAvailableParametersExceptSelected(this.gatewaySerial)
      .pipe(distinctUntilChanged(isEqual), takeUntil(this.ngUnsubscribe$))
      .subscribe((parameters) => {
        this.availableParametersExceptSelected = parameters
          ? DiagnosticsHelpers.mapVDDListToDisplay(parameters)
          : [];
        this.availableParametersExceptSelected.map((parameter) => {
          parameter.viewValue = this.translate.instant(parameter.viewValue);
        });
        this.cdr.detectChanges();
      });

    this.remoteDiagnosticsStore
      .getSelectedControlUnit(this.gatewaySerial)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((selectedControlUnit) => {
        this.currentControlUnit = selectedControlUnit;
        this.cdr.detectChanges();
      });
  }

  addParameters(values: Option[]): void {
    this.selectedVdds = [];
    const vdds: RemoteDiagnosticsVddListParameter[] = values.map((value) => {
      return {
        name: value.viewValueThree,
        value: value.viewValue,
        vddInterval: value.viewValueTwo,
        isWrite: value.isWrite,
        isRead: value.isRead,
      };
    });
    this.gtmService.track(
      'onSelect',
      `Parameter names: ${vdds.map((vdd) => vdd.name).join(', ')}`,
      'diagnosticsPage'
    );
    this.selectedVdds = vdds;
  }

  onControlUnitOptionChange(selectedControlUnit: RemoteDiagnosticsControlUnit): void {
    this.remoteDiagnosticsStore.selectControlUnit(selectedControlUnit, this.gatewaySerial);
    this.remoteDiagnosticsStore.openLogicalLink(this.gatewaySerial, selectedControlUnit.value);
    this.selectedVdds = [];
  }

  compareControlUnits(
    firstControlUnit: RemoteDiagnosticsControlUnit,
    secondControlUnit: RemoteDiagnosticsControlUnit
  ): boolean {
    return firstControlUnit?.value === secondControlUnit?.value;
  }

  closeModal(): void {
    this.loading = true;

    // Timeout is required, otherwise vi-loading won't be rendered
    timer(100)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(() => {
        this.selectedVdds.forEach((vdd) => {
          this.remoteDiagnosticsStore.addParameter(
            this.currentControlUnit,
            vdd,
            this.gatewaySerial
          );
          this.gtmService.track('onSelect', `Parameter name: ${vdd.name}`, 'diagnosticsPage');
        });
        this.dialogRef.close();
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
